import { gql } from '@apollo/client';

import '@cobliteam/common-types';

import type {
  CreateMaintenanceHistoryInput,
  CreateMaintenancePartsInput,
  CreateReminderInput,
  DeleteMaintenanceHistoryInput,
  DeleteMaintenancePartsInput,
  DeleteMaintenanceReminderInput,
  GetLatestMaintenanceInput,
  GetMaintenanceHistoryInput,
  GetMaintenancePartsInput,
  GetReminderListInput,
  GetReminderStatusCountInput,
  ImportBatchResponse,
  ImportMaintenanceBatchInput,
  Maintenance,
  MaintenancePartResponse,
  MaintenanceReminderResponse,
  MaintenanceResponse,
  Part,
  ReminderDto,
  ReminderStatusCount,
  ReminderTitle,
  UpdateMaintenanceHistoryInput,
  UpdateMaintenancePartsInput,
  UpdateReminderInput,
  ValidateMaintenanceImportInput,
  ValidateMaintenanceImportResponse,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import TimeZone from '../../request/Timezone';
import type { GraphQLRestDataSourceError } from '../../request/Types';

export class GraphQLMaintenanceRequester {
  public getMaintenanceHistory = (request: GetMaintenanceHistoryInput): Promise<MaintenanceResponse> =>
    graphQLRequester
      .query<{
        maintenanceHistory: MaintenanceResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query maintenanceHistory($input: GetMaintenanceHistoryInput!) {
            maintenanceHistory(input: $input) {
              count
              rows {
                createdBy
                description
                id
                occurrenceAt
                odometerInKms
                totalPrice
                types
                updatedAt
                updatedBy
                recurrences {
                  description
                  everyKms
                  everyDays
                }
                parts {
                  description
                  id
                  part {
                    description
                    id
                  }
                  price
                  quantity
                }
                vehicle {
                  id
                  licensePlate
                  alias
                  groups {
                    id
                    name
                  }
                  branding {
                    brand
                    model
                    year
                  }
                  size
                  tags {
                    name
                  }
                }
              }
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.maintenanceHistory;
      });

  public getMaintenanceParts = (request: GetMaintenancePartsInput): Promise<MaintenancePartResponse> =>
    graphQLRequester
      .query<{
        maintenanceParts: MaintenancePartResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query maintenanceParts($input: GetMaintenancePartsInput!) {
            maintenanceParts(input: $input) {
              count
              rows {
                description
                id
                part {
                  description
                  id
                }
                price
                quantity
              }
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.maintenanceParts;
      });

  public createMaintenanceHistory = (request: CreateMaintenanceHistoryInput): Promise<Maintenance> =>
    graphQLRequester
      .mutate<{
        createMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation createMaintenanceHistory(
            $input: CreateMaintenanceHistoryInput!
          ) {
            createMaintenanceHistory(input: $input) {
              id
              types
              occurrenceAt
              totalPrice
              odometerInKms
              description
              parts {
                id
                description
                part {
                  description
                  id
                }
                price
                quantity
              }
              recurrences {
                description
                everyKms
              }
              vehicle {
                id
                licensePlate
                branding {
                  brand
                  model
                  year
                }
                size
              }
              createdBy
              updatedAt
              updatedBy
              createdBy
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.createMaintenanceHistory;
      });

  public updateMaintenanceHistory = (request: UpdateMaintenanceHistoryInput): Promise<Maintenance> =>
    graphQLRequester
      .mutate<{
        updateMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation updateMaintenanceHistory(
            $input: UpdateMaintenanceHistoryInput!
          ) {
            updateMaintenanceHistory(input: $input) {
              id
              types
              occurrenceAt
              totalPrice
              odometerInKms
              description
              parts {
                id
                description
                price
                quantity
              }
              recurrences {
                description
                everyKms
              }
              updatedAt
              updatedBy
              createdBy
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateMaintenanceHistory;
      });

  public deleteMaintenanceHistory = (request: DeleteMaintenanceHistoryInput): Promise<null> =>
    graphQLRequester
      .mutate<{
        deleteMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation deleteMaintenanceHistory(
            $input: DeleteMaintenanceHistoryInput!
          ) {
            deleteMaintenanceHistory(input: $input)
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return null;
      });

  public createMaintenanceParts = (request: CreateMaintenancePartsInput): Promise<Part | null> =>
    graphQLRequester
      .mutate<{
        createMaintenanceParts: Part;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation createMaintenanceParts($input: CreateMaintenancePartsInput!) {
            createMaintenanceParts(input: $input) {
              id
              description
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          if (result.errors[0]?.status === 409) {
            return null;
          }
          throw result.errors[0];
        }

        return result.createMaintenanceParts;
      });

  public updateMaintenanceParts = (request: UpdateMaintenancePartsInput): Promise<Part> =>
    graphQLRequester
      .mutate<{
        updateMaintenanceParts: Part;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation updateMaintenanceParts($input: UpdateMaintenancePartsInput) {
            updateMaintenanceParts(input: $input) {
              id
              description
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateMaintenanceParts;
      });

  public deleteMaintenanceParts = (request: DeleteMaintenancePartsInput): Promise<null> =>
    graphQLRequester
      .mutate<{
        deleteMaintenanceParts: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation deleteMaintenanceParts($input: DeleteMaintenancePartsInput) {
            deleteMaintenanceParts(input: $input)
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return null;
      });

  public validateMaintenanceImport = (input: ValidateMaintenanceImportInput): Promise<ValidateMaintenanceImportResponse> =>
    graphQLRequester
      .query<{
        validateMaintenanceImport: ValidateMaintenanceImportResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query ValidateMaintenanceImport($input: ValidateMaintenanceImportInput!) {
            validateMaintenanceImport(input: $input) {
              validRows {
                vehicle {
                  id
                }
                type
                occurrenceAt
                odometerInKms
                parts {
                  partName
                  unitCost
                  quantity
                  partObservation
                }
                maintenanceObservations
              }
              invalidRows {
                maintenanceData {
                  licensePlate
                  type
                  occurrenceAt
                  odometerInKms
                  maintenanceObservation
                  items {
                    serviceItemName
                    unitCost
                    quantity
                    description
                    rowIndex
                  }
                }
                errors {
                  error
                  column {
                    name
                    position
                  }
                }
                items {
                  itemsData {
                    partName
                    unitCost
                    quantity
                    partObservation
                  }
                  rowIndex
                  errors {
                    error
                    column {
                      name
                      position
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.validateMaintenanceImport;
      });

  public importMaintenanceHistory = (request: ImportMaintenanceBatchInput[]): Promise<ImportBatchResponse> =>
    graphQLRequester
      .mutate<{
        importMaintenanceHistory: ImportBatchResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation importMaintenanceHistory($input: [ImportMaintenanceBatchInput!]!) {
            importMaintenanceHistory(input: $input) {
              count
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.importMaintenanceHistory;
      });

  public createMaintenanceReminder = (request: CreateReminderInput): Promise<ReminderDto[]> =>
    graphQLRequester
      .mutate<{
        createMaintenanceReminder: ReminderDto[];
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
            mutation createMaintenanceReminder($input: CreateReminderInput!) {
              createMaintenanceReminder(input: $input) {
                id
                vehicle {
                  size
                  id
                  licensePlate
                }
                title
                description
                recurrenceInKm
                nextOccurrenceInKm
                recurrenceInDays
                nextOccurrenceDate
                status {
                  name
                  km
                  days
                }
                lastMaintenanceDate
              }
            }
          `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.createMaintenanceReminder;
      });

  public updateMaintenanceReminder = (request: UpdateReminderInput): Promise<ReminderDto> =>
    graphQLRequester
      .mutate<{
        updateMaintenanceReminder: ReminderDto;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
            mutation updateMaintenanceReminder($input: UpdateReminderInput!) {
              updateMaintenanceReminder(input: $input) {
                id
                vehicle {
                  id
                  size
                  licensePlate
                }
                title
                description
                recurrenceInKm
                nextOccurrenceInKm
                recurrenceInDays
                nextOccurrenceDate
                status {
                  name
                  km
                  days
                }
                lastMaintenanceDate
              }
            }
          `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateMaintenanceReminder;
      });

  public deleteMaintenanceReminder = (request: DeleteMaintenanceReminderInput): Promise<boolean> =>
    graphQLRequester
      .mutate<{
        deleteMaintenanceReminder: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
            mutation deleteMaintenanceReminder(
              $input: DeleteMaintenanceReminderInput!
            ) {
              deleteMaintenanceReminder(input: $input)
            }
            `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.deleteMaintenanceReminder;
      });

  public getReminderTitles = (): Promise<ReminderTitle[]> =>
    graphQLRequester
      .query<{
        reminderTitles: ReminderTitle[];
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query reminderTitles {
            reminderTitles {
              id
              value
            }
          }
        `,
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.reminderTitles;
      });

  public getRemindersList = (request: GetReminderListInput): Promise<MaintenanceReminderResponse> =>
    graphQLRequester
      .query<{
        reminderList: MaintenanceReminderResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query ReminderList($input: GetReminderListInput!) {
            reminderList(input: $input) {
              count
              rows {
                id
                vehicle {
                  id
                  alias
                  branding {
                    brand
                    model
                    year
                    type
                  }
                  deviceType
                  licensePlate
                  model {
                    brand
                    name
                    year
                    version
                    isSuggestion
                    type
                  }
                  tags {
                    name
                  }
                  groups {
                    id
                    name
                  }
                }
                title
                description
                recurrenceInKm
                nextOccurrenceInKm
                recurrenceInDays
                nextOccurrenceDate
                type
                status {
                  name
                  km
                  days
                }
                lastMaintenanceDate
                fleetId
              }
            }
          }
        `,
        variables: {
          input: {
            ...request,
            timezone: TimeZone.getTimezone(),
          },
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.reminderList;
      });

  public getRemindersStatusCount = (request: GetReminderStatusCountInput): Promise<ReminderStatusCount> =>
    graphQLRequester
      .query<{
        reminderStatusSummary: ReminderStatusCount;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query ReminderStatusSummary($input: GetReminderStatusCountInput!) {
            reminderStatusSummary(input: $input) {
              onTime
              warning
              delayed
            }
          }
        `,
        variables: {
          input: {
            ...request,
            timezone: TimeZone.getTimezone(),
          },
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.reminderStatusSummary;
      });

  public getLatestMaintenanceByVehicle(input: GetLatestMaintenanceInput): Promise<Maintenance | null> {
    return graphQLRequester
      .query<{
        latestMaintenanceByVehicle: Maintenance | null;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query latestMaintenanceByVehicle($input: GetLatestMaintenanceInput!) {
            latestMaintenanceByVehicle(input: $input) {
              id
              occurrenceAt
              odometerInKms
              description
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.latestMaintenanceByVehicle;
      });
  }
}

export default GraphQLMaintenanceRequester;
